import React from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { PieChartTypeEnum } from '../../../../../../models/enums/pie-chart-type-enum';
import { ValuePercents } from '../../../../value/percents/value-precents';
import { ValueString } from '../../../../value/string/value-string';
import { PieChartDataMobxDto } from '../../../../../../models/mobx/dtos/statistic/pie-chart-data-mobx-dto';
import { cn } from '../../../../../../utils/tailwind';
import { getOpacity } from '../../../../../../theme/utils/get-rgb';

export interface BaseLegendItemProps {
    data: PieChartDataMobxDto;
    type?: PieChartTypeEnum;
    color: string;
    isFree: boolean;
    isFullScreen?: boolean;
}

export const BaseLegendItem: React.FC<BaseLegendItemProps> = observer(
    ({ data, type, color, isFree, isFullScreen }: BaseLegendItemProps) => {
        const { t, i18n } = useTranslation();
        const theme = useTheme();

        return (
            <div
                className={cn(
                    'flex flex-1 items-center justify-between gap-[1rem] border-t border-border py-xsmall first:border-t-0',
                    isFullScreen && 'lg:min-h-[8rem]',
                )}
            >
                <div
                    className={cn(
                        'relative flex w-full items-center overflow-hidden text-ellipsis whitespace-nowrap text-vsmall font-medium text-text-muted',
                        'xxl:text-medium',
                        isFullScreen && 'lg:text-xlarge',
                    )}
                    style={{
                        backgroundImage: `linear-gradient(90deg, ${getOpacity(
                            theme.palette.background.body,
                            0,
                        )} 0%, ${getOpacity(theme.palette.background.body, 1)} 100%)`,
                        backgroundPosition: 'right',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: '2rem 100%',
                    }}
                >
                    <div
                        className={cn(
                            'mr-[0.5rem] h-[1.2rem] w-[1.2rem] min-w-[1.2rem] rounded-full xxl:mr-[0.8rem] xxl:h-[2rem] xxl:w-[2rem] xxl:min-w-[2rem]',
                            isFullScreen && 'lg:mr-[2rem] lg:h-[4rem] lg:w-[4rem] lg:min-w-[4rem]',
                            isFree && 'blur-[0.3rem]',
                        )}
                        style={{ backgroundColor: color }}
                    />
                    <ValueString
                        text={
                            i18n.exists(`pages.index.${type}.${data.id}`)
                                ? t(`pages.index.${type}.${data.id}`)
                                : data.id
                        }
                        locked={isFree}
                        mode="pure"
                    />
                </div>
                <div
                    className={cn(
                        'ml-auto whitespace-nowrap text-vsmall font-bold text-brand-secondary xxl:text-medium',
                        isFullScreen && 'lg:text-xlarge',
                    )}
                >
                    <ValuePercents amount={data.value} fractionDigits={1} locked={isFree} mode="pure" />
                </div>
            </div>
        );
    },
);
